// What aldoo.com gives the framework's order screens (aldoo-ra/StoreOrders):
// its orders API (a seller's orders, vendor access), the vendor portal's
// words, the site language's dates, and the pickup office with its map.
import QRCode from "react-qr-code"
import React, { useState } from "react"
import { MapPinIcon } from "@heroicons/react/24/outline"
import OfficeMapDialog from "../Storefront/checkout/shipping/OfficeMapDialog"
import OfficePicker from "../Storefront/checkout/shipping/OfficePicker"
import { officeAddress } from "../Storefront/checkout/shipping/providers"
import { ListOrders, GetOrder, GetClientOrders, ShipOrder, SetOrderStatus, RefundOrder, ReturnOrder, ResolveReturnRequest, ResolveCancelRequest, ModifyOrder, SearchOrderProducts, MoveOrderSellers, MoveOrderProducts, MoveOrderMatch, MoveOrderQuote, MoveOrder, SetOrderTracking, CheckOrderTracking, SaveVendorProfile, GetOrderInvoice, GetOrderInvoiceFile, UploadOrderInvoice, RemoveOrderInvoice } from "../AdminShared/api"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { ORDERS } from "../Vendor/texts/bundles"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import ShipmentDialog, { ShipmentPanel, printLabel, labelSizes, sizeName } from "./ShipmentDialog"
import { QrCodeIcon } from "@heroicons/react/24/outline"

// the company's return address, added while answering a return request: its Delivery settings
// (profile_save — an admin member of the company, or the platform admin)
const SaveOrderReturnAddress = (order_id, address, order) => (order?.sellerId ? SaveVendorProfile(String(order.sellerId), { returnAddress: address }) : Promise.resolve({ error: "no_seller", message: "This order has no seller" }))

export const ORDERS_API = { list: ListOrders, get: GetOrder, clientOrders: GetClientOrders, ship: ShipOrder, setStatus: SetOrderStatus, refund: RefundOrder, markReturned: ReturnOrder, resolveRequest: ResolveReturnRequest, resolveCancel: ResolveCancelRequest, modify: ModifyOrder, modifyProducts: SearchOrderProducts, moveSellers: MoveOrderSellers, moveProducts: MoveOrderProducts, moveMatch: MoveOrderMatch, moveQuote: MoveOrderQuote, move: MoveOrder, setTracking: SetOrderTracking, checkTracking: CheckOrderTracking, setReturnAddress: SaveOrderReturnAddress, invoice: GetOrderInvoice, invoiceFile: GetOrderInvoiceFile, uploadInvoice: UploadOrderInvoice, removeInvoice: RemoveOrderInvoice }

export const useOrderTexts = () => usePortalTexts(ORDERS)

export const useOrderLocale = () => (useSiteLanguage() === "bg" ? "bg-BG" : "en-GB")

function Pickup({ office, provider, t, locale }) {
  const [mapOpen, setMapOpen] = useState(false)
  return (
    <>
      <div className="font-medium">{office.name}</div>
      {office.address && <div className="text-black/70">{officeAddress(office)}</div>}
      {office.type === "shop" ? (
        // collected at the company's own shop: no courier, no office id
        <div className="text-black/70">{t`The buyer collects it at your shop`}{office.hours ? ` · ${office.hours}` : ""}</div>
      ) : (
      <div className="text-black/70">
        {t(office.type === "locker" ? "%s locker id:" : "%s office id:", provider?.providerName || t`Courier`)} <span className="font-mono font-semibold text-black">{office.id}</span>
      </div>
      )}
      <button
        type="button"
        onClick={() => setMapOpen(true)}
        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-admin_bordered text-xs text-admin_text hover:border-admin_primary hover:text-admin_primary print:hidden"
        data-testid="order-office-map"
      >
        <MapPinIcon className="w-4 h-4" />
        {t`See on map`}
      </button>
      <OfficeMapDialog isOpen={mapOpen} onClose={() => setMapOpen(false)} providerId={provider?.providerId} office={office} language={locale === "bg-BG" ? "bg" : "en"} variant="admin_centered" />
    </>
  )
}

export const renderPickup = (props) => <Pickup {...props} />

// the printed order sheet's code into the buyer's order (react-qr-code: aldoo.com's, not the framework's)
export const renderQR = (value) => <QRCode value={value} size={96} level="M" />

// the Ship action makes the waybill with the courier (Speedy, Econt: the company's account), and the
// waybill made stays on the Delivery tab with its label and its cancellation (ShipmentDialog)
export const renderShip = (props) => <ShipmentDialog {...props} />
export const renderShipment = (props) => <ShipmentPanel {...props} />

// moving an order to another seller (an Aldoo admin's): the office or locker it goes to, picked from the
// courier's list as the checkout picks it
export const renderOfficePicker = ({ isOpen, onClose, option, city, near, onPick }) => <OfficePicker isOpen={isOpen} onClose={onClose} option={option} city={city} near={near} language="bg" onPick={onPick} />

// Print offers the waybill's label too, while the order has one: a label printer's size and A4 for Speedy
// and Pigeon Express, Econt's one label
export const extraPrints = ({ order, t }) => {
  const s = order?.shipment
  if (!s?.number || s.canceledAt) return []
  const sizes = labelSizes(s.providerId)
  return sizes.map((size) => ({
    value: `label_${size}`,
    name: sizes.length > 1 ? t("Waybill %s", sizeName(s.providerId, size)) : t`Waybill`,
    hint: t("The label to stick on the parcel · %s", s.number),
    icon: QrCodeIcon,
    run: () => printLabel(order, size, t),
  }))
}
